/**
 * Mobility auth — the OTP login flow, the session probe (`/auth/me`), and the
 * `useSession` hook the shell + route guards read.
 *
 * FLOW (M-1): `sendOtp(email)` → uniform "if registered" 200 (no enumeration
 * oracle); `verifyOtp(email, otp)` returns a session only in test (RS256 mint).
 * Production sessions are bank RS256 JWTs (`?JWT_TOKEN=` or SSO exchange).
 * which we persist in sessionStorage (lib/token-store) and returns the identity;
 * `me()` re-resolves the CURRENT principal (roles included) from the DB per
 * request — so a revoked grant / INACTIVE roster changes what the portal shows
 * within the roster cache TTL. `logout()` just clears the token (there is no
 * server session to destroy — the JWT is stateless).
 *
 * NEVER-LOG-PII / NEVER-LOG-TOKENS: nothing here logs the email, code, or token.
 */

import { useCallback, useEffect, useState } from 'react'
import { mobilityClient } from './api'
import { clearToken, getToken, setToken, stashToken, subscribe } from './token-store'
import { exchangeSsoToken, type Role } from './mobility'

export type { Role } from './mobility'

// ── Response DTOs ────────────────────────────────────────────────────────────────

export interface OtpSendResult {
  message: string
  expires_in_seconds: number
}

export interface OtpVerifyResult {
  token: string
  roster_id: string
  entity_id: string
  employee_code: string
}

/** The `/auth/me` principal — the SPA's session identity. */
export interface Session {
  roster_id: string
  entity_id: string
  employee_code: string
  roles: Role[]
  entity_name: string | null
}

// ── Auth actions ─────────────────────────────────────────────────────────────────

/** Send an OTP to a corporate email. Always resolves 200 (uniform message, M-14). */
export async function sendOtp(email: string): Promise<OtpSendResult> {
  return mobilityClient.post<OtpSendResult>('/auth/otp/send', { email })
}

/**
 * Verify the OTP: on success the service mints the session JWT — we persist it
 * (so every subsequent api call is authenticated) and return the identity.
 * A wrong/expired code throws a typed ApiError (UNAUTHORIZED / OTP_EXPIRED /
 * OTP_MAX_ATTEMPTS) the caller surfaces as an inline alert.
 */
export async function verifyOtp(email: string, otp: string): Promise<OtpVerifyResult> {
  const result = await mobilityClient.post<OtpVerifyResult>('/auth/otp/verify', { email, otp })
  setToken(result.token)
  return result
}

/** Resolve the current principal (roles resolved live from the DB, M-1). */
export async function me(): Promise<Session> {
  return mobilityClient.get<Session>('/auth/me')
}

/** Clear the session token (stateless JWT — nothing to revoke server-side). */
export function logout(): void {
  clearToken()
}

// ── JWT_TOKEN query (any route) ──────────────────────────────────────────────

/** The query parameter the bank IdP (or a pasted link) lands the session JWT in. */
export const SSO_TOKEN_PARAM = 'JWT_TOKEN'

/**
 * Pull `JWT_TOKEN` off the CURRENT URL (any path) and strip it immediately via
 * `history.replaceState` so it never stays in the address bar. Returns the raw
 * token (null when absent/empty). The caller persists it as the session token
 * (bank RS256 JWTs are verified by the API).
 */
export function takeSsoTokenFromUrl(): string | null {
  const url = new URL(window.location.href)
  if (!url.searchParams.has(SSO_TOKEN_PARAM)) return null
  const token = url.searchParams.get(SSO_TOKEN_PARAM)
  url.searchParams.delete(SSO_TOKEN_PARAM)
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
  return token === null || token === '' ? null : token
}

/**
 * If `JWT_TOKEN` is on this URL, stash it as the session token. Safe to call
 * during render of a parent layout (stashes without emitting) so child route
 * guards see a token on the same pass. Returns the captured token or null.
 */
export function ingestJwtTokenFromUrl(): string | null {
  const token = takeSsoTokenFromUrl()
  if (token === null) return null
  stashToken(token)
  return token
}

/**
 * Exchange the external bank-IdP JWT for a mobility session and persist the
 * MINTED token exactly like {@link verifyOtp} does (same response shape by
 * backend contract). The external token is consumed in memory and never
 * stored; a bad/expired/replayed token throws a typed 401 {@link ApiError}
 * the bootstrap surfaces on the login page.
 */
export async function signInWithSso(externalToken: string): Promise<OtpVerifyResult> {
  const result = await exchangeSsoToken(externalToken)
  setToken(result.token)
  return result
}

// ── Role → landing path ──────────────────────────────────────────────────────────

/**
 * The default landing path for a signed-in principal, by highest-privilege role.
 * A principal may hold several roles (→ the shell's portal switcher); this picks
 * the most capable surface as the post-login home. EMPLOYEE (implicit for every
 * active roster) falls through to the employee Home screen.
 */
export function homeForRoles(roles: readonly Role[]): string {
  if (roles.includes('HR_ADMIN')) return '/admin'
  if (roles.includes('HR_PARTNER')) return '/recruiter'
  return '/home'
}

// ── useSession ───────────────────────────────────────────────────────────────────

export interface UseSessionResult {
  session: Session | null
  roles: Role[]
  loading: boolean
  signedIn: boolean
  /** Re-resolve `/auth/me` (e.g. after a role change). */
  refresh: () => Promise<void>
  /** Clear the token and drop the in-memory session (→ guards bounce to /login). */
  logout: () => void
}

/**
 * The session hook: on mount (and whenever the token changes — a 401 deep in the
 * api client clears it and notifies subscribers), probe `/auth/me`. No token →
 * signed out immediately (no wasted request). A failed probe → signed out (the
 * api client already cleared an expired token on the 401).
 */
export function useSession(): UseSessionResult {
  const [session, setSession] = useState<Session | null>(null)

  const resolve = useCallback(async () => {
    if (getToken() === null) {
      setSession(null)
      return
    }
    try {
      setSession(await me())
    } catch {
      setSession(null)
    }
  }, [])

  useEffect(() => {
    void resolve()
    const unsubscribe = subscribe(() => {
      void resolve()
    })
    return unsubscribe
  }, [resolve])

  const doLogout = useCallback(() => {
    logout()
  }, [])

  const tokenPresent = getToken() !== null
  return {
    session,
    roles: session?.roles ?? [],
    loading: tokenPresent && session === null,
    signedIn: session !== null,
    refresh: resolve,
    logout: doLogout,
  }
}
